import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, IconButton,
  CircularProgress, Divider, Chip,
} from '@mui/material';
import {
  Close, CheckCircle, Cancel, WorkspacePremium, OpenInNew,
  AddCard, ContactMail,
} from '@mui/icons-material';
import { useCreateCheckoutSessionMutation, useCreateTopupCheckoutMutation } from '../../store/api/authApi';
import type { RootState } from '../../store/store';

// ── Types ─────────────────────────────────────────────────────────────────────

export type UpgradeReason = 'ai_fix' | 'scan_limit' | 'repo_limit' | 'general' | 'refactor' | 'fix_limit' | 'issue_list';

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  reason?: UpgradeReason;
  /** Raw API error message — shown verbatim if present */
  errorMessage?: string;
}

// ── Plan comparison data ──────────────────────────────────────────────────────

const FREE_FEATURES = [
  '1 repository',
  '3 scans / month',
  'Static analysis only',
  'No AI fixes',
];

const PRO_FEATURES = [
  '5 repositories',
  '20 scans / month',
  'Full AI-powered analysis',
  '50 AI fixes / month',
  'Automated PRs',
  'Priority insights',
];

const REASON_CONTEXT: Record<UpgradeReason, { title: string; subtitle: string }> = {
  ai_fix: {
    title: 'AI fixes are a Pro feature',
    subtitle: 'Upgrade to let KodeGauge write the fix and raise a PR — automatically.',
  },
  scan_limit: {
    title: "You've reached your monthly scan limit",
    subtitle: 'Upgrade to Pro for 20 scans per month and never hit a wall mid-sprint.',
  },
  repo_limit: {
    title: "You've reached your repository limit",
    subtitle: 'Upgrade to Pro to connect up to 5 repositories.',
  },
  general: {
    title: 'Unlock the full power of KodeGauge',
    subtitle: 'Get AI fixes, more scans, and automated PRs with Pro.',
  },
  refactor: {
    title: 'Refactor plans are a Pro feature',
    subtitle: 'Upgrade to let KodeGauge analyse large files and propose a structured split into focused modules.',
  },
  issue_list: {
    title: "You're seeing a preview of your findings",
    subtitle: 'The free plan shows your top 3 issues per category. Upgrade to see every finding, with full detail and AI-guided fixes.',
  },
  fix_limit: {
    title: "You've used all your AI fixes this month",
    subtitle: 'Your 50 monthly AI fixes are exhausted. Top up instantly or talk to us about unlimited fixes.',
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  open,
  onClose,
  reason = 'general',
  errorMessage,
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [createCheckout, { isLoading: isUpgrading }] = useCreateCheckoutSessionMutation();
  const [createTopup, { isLoading: isTopuping }] = useCreateTopupCheckoutMutation();
  const [checkoutError, setCheckoutError] = useState('');

  const isLoading = isUpgrading || isTopuping;
  const context = REASON_CONTEXT[reason];
  const isFixLimit = reason === 'fix_limit';

  const handleUpgrade = async () => {
    setCheckoutError('');
    try {
      const { checkout_url } = await createCheckout({
        existing_user: true,
        customer_email: user?.email ?? user?.github_email ?? undefined,
        user_id: user?.id ?? undefined,
      }).unwrap();
      window.location.href = checkout_url;
    } catch {
      setCheckoutError('Could not start checkout. Please try again.');
    }
  };

  const handleTopup = async () => {
    setCheckoutError('');
    try {
      const { checkout_url } = await createTopup().unwrap();
      window.location.href = checkout_url;
    } catch {
      setCheckoutError('Could not start checkout. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        px: 3, pt: 3, pb: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 40, height: 40, borderRadius: 2,
            bgcolor: 'primary.main', color: 'white', flexShrink: 0,
          }}>
            <WorkspacePremium fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {context.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {context.subtitle}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ ml: 1, mt: -0.5 }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {/* Optional raw API message */}
        {errorMessage && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#fff3e0', border: '1px solid', borderColor: '#ffcc80' }}>
            <Typography variant="body2" color="warning.dark">{errorMessage}</Typography>
          </Box>
        )}

        {isFixLimit ? (
          /* ── Fix-limit: two options ───────────────────────────────────── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Top-up option */}
            <Box sx={{
              p: 2, borderRadius: 2, border: '2px solid',
              borderColor: 'primary.main', bgcolor: '#e3f2fd',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <AddCard sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                  Buy 10 more fixes — $10
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                One-time purchase. Credits never expire and roll over to next month.
              </Typography>
            </Box>

            {/* Enterprise option */}
            <Box sx={{
              p: 2, borderRadius: 2, border: '1px solid',
              borderColor: 'divider', bgcolor: 'grey.50',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                <ContactMail sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  Enterprise — unlimited fixes
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                Custom pricing, SLA, and dedicated support. Talk to us.
              </Typography>
            </Box>
          </Box>
        ) : (
          /* ── Default: plan comparison ─────────────────────────────────── */
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {/* Free */}
            <Box sx={{
              flex: 1, p: 2, borderRadius: 2, border: '1px solid',
              borderColor: 'divider', bgcolor: 'grey.50',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">Free</Typography>
                <Chip label="Current" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {FREE_FEATURES.map((f) => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Cancel sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>{f}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Pro */}
            <Box sx={{
              flex: 1, p: 2, borderRadius: 2, border: '2px solid',
              borderColor: 'primary.main', bgcolor: '#e3f2fd',
              position: 'relative',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} color="primary.main">Pro</Typography>
                <Chip label="Recommended" color="primary" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {PRO_FEATURES.map((f) => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CheckCircle sx={{ fontSize: 14, color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{f}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {checkoutError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5 }}>
            {checkoutError}
          </Typography>
        )}

        <Divider sx={{ mt: 2.5, mb: 0 }} />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="text" size="small" onClick={onClose}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
          disabled={isLoading}
        >
          Maybe later
        </Button>

        {isFixLimit ? (
          <>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<ContactMail fontSize="small" />}
              href="mailto:hello@kodegauge.com?subject=Enterprise%20pricing"
              target="_blank"
              sx={{ textTransform: 'none', borderRadius: 1.5 }}
            >
              Contact us
            </Button>
            <Button
              variant="contained"
              size="medium"
              disableElevation
              startIcon={isTopuping ? <CircularProgress size={14} color="inherit" /> : <AddCard fontSize="small" />}
              onClick={handleTopup}
              disabled={isLoading}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 2.5 }}
            >
              {isTopuping ? 'Redirecting…' : 'Buy 10 fixes — $10'}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            size="medium"
            disableElevation
            startIcon={isUpgrading ? <CircularProgress size={14} color="inherit" /> : <OpenInNew fontSize="small" />}
            onClick={handleUpgrade}
            disabled={isLoading}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 2.5 }}
          >
            {isUpgrading ? 'Redirecting…' : 'Upgrade to Pro'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeModal;
